import { TranslationKeys } from 'localization';
import { WorkOrderModuleCategoriesSettings } from 'lane-shared/domains/workOrder';
import {
  CoverageHoursCategoryOption,
  CoverageHoursIssueTypeOption,
} from './useCoverageHoursOptions';

export type DropdownOption = { label: string; value: string };

export const ALL_ISSUE_TYPES_VALUE = 'ALL_ISSUE_TYPES';
export const NONE_ISSUE_TYPE_VALUE = 'NONE';

function issueTypesForCategory(
  issueTypes: CoverageHoursIssueTypeOption[],
  categoryName: string
): CoverageHoursIssueTypeOption[] {
  return issueTypes.filter(option => option.category === categoryName);
}

type CoverageHoursIssueTypeWithIssue = CoverageHoursIssueTypeOption & {
  issue: string;
};

function hasIssue(
  option: CoverageHoursIssueTypeOption
): option is CoverageHoursIssueTypeWithIssue {
  return option.issue !== null;
}

// Whether this category's category-level (no-issue) routing already exists -
// exposed so a caller can decide whether an "All Issue Types" save should
// also fold in a None creation, without duplicating the lookup itself.
function isNoneUsedFor(
  issueTypes: CoverageHoursIssueTypeOption[],
  categoryName: string
): boolean {
  return issueTypesForCategory(issueTypes, categoryName).some(
    option => option.issue === null && option.isUsed
  );
}

// "All Issue Types" is the catch-all: if the category-level (no-issue)
// routing doesn't exist yet either, it should be folded into the same save
// rather than left as a separate option the user has to come back for.
export function shouldCreateNoneWithAllIssueTypes(
  issueTypeValue: string,
  selectedCategory: WorkOrderModuleCategoriesSettings | undefined,
  issueTypes: CoverageHoursIssueTypeOption[]
): boolean {
  return (
    issueTypeValue === ALL_ISSUE_TYPES_VALUE &&
    selectedCategory !== undefined &&
    !isNoneUsedFor(issueTypes, selectedCategory.firendlyName ?? '')
  );
}

export function getIssueTypeInfoKey(
  issueTypeValue: string
): TranslationKeys | null {
  if (issueTypeValue === ALL_ISSUE_TYPES_VALUE) {
    return 'web.admin.serviceRequest.settings.coverageHours.create.allIssueTypesInfo';
  }

  if (issueTypeValue === NONE_ISSUE_TYPE_VALUE) {
    return 'web.admin.serviceRequest.settings.coverageHours.create.noneIssueTypeInfo';
  }

  return null;
}

export function buildCategoryItems(
  categories: WorkOrderModuleCategoriesSettings[],
  categoryOptions: CoverageHoursCategoryOption[]
): DropdownOption[] {
  const creatableNames = new Set(
    categoryOptions.filter(o => o.hasCreatableOptions).map(o => o.name)
  );

  return categories
    .filter(c => (c.firendlyName ?? '').trim() !== '')
    .filter(c => creatableNames.has(c.firendlyName))
    .map(c => ({ label: c.firendlyName, value: c.firendlyName }));
}

export function findSelectedCategory(
  categories: WorkOrderModuleCategoriesSettings[],
  categoryValue: string | null
): WorkOrderModuleCategoriesSettings | undefined {
  return categories.find(c => c.firendlyName === categoryValue);
}

// An empty issues list means a single category-level routing with no issue —
// this is the exact contract InputCreateCoverageHours.issues documents.
// "All Issue Types" resolves to only the still-creatable issues: submitting
// one that already has a routing is exactly the failure this filtering
// feature exists to prevent.
export function resolveIssues(
  issueTypeValue: string,
  selectedCategory: WorkOrderModuleCategoriesSettings | undefined,
  issueTypes: CoverageHoursIssueTypeOption[]
): string[] {
  if (issueTypeValue === NONE_ISSUE_TYPE_VALUE) {
    return [];
  }

  if (issueTypeValue === ALL_ISSUE_TYPES_VALUE) {
    if (!selectedCategory) {
      return [];
    }

    return issueTypesForCategory(issueTypes, selectedCategory.firendlyName ?? '')
      .filter(hasIssue)
      .filter(option => !option.isUsed)
      .map(option => option.issue);
  }

  return [issueTypeValue];
}

// The pair being edited is fixed, and its own routing already exists - so its
// values are filtered out of the creatable items offered on create. The
// disabled selects showing it get the one item each needs to show it selected.
export function buildLockedCategoryItems(
  categoryValue: string | null
): DropdownOption[] {
  return categoryValue ? [{ label: categoryValue, value: categoryValue }] : [];
}

export function buildLockedIssueTypeItems(
  issueTypeValue: string,
  t: (key: string) => string
): DropdownOption[] {
  const label =
    issueTypeValue === NONE_ISSUE_TYPE_VALUE
      ? t('web.admin.serviceRequest.settings.coverageHours.create.none')
      : issueTypeValue;

  return [{ label, value: issueTypeValue }];
}

export function buildIssueTypeItems(
  selectedCategory: WorkOrderModuleCategoriesSettings | undefined,
  issueTypes: CoverageHoursIssueTypeOption[],
  t: (key: string) => string
): DropdownOption[] {
  if (!selectedCategory) {
    return [];
  }

  const forCategory = issueTypesForCategory(
    issueTypes,
    selectedCategory.firendlyName ?? ''
  );

  const { usedIssues, creatableIssues } = forCategory.filter(hasIssue).reduce<{
    usedIssues: CoverageHoursIssueTypeWithIssue[];
    creatableIssues: CoverageHoursIssueTypeWithIssue[];
  }>(
    (acc, option) => {
      (option.isUsed ? acc.usedIssues : acc.creatableIssues).push(option);

      return acc;
    },
    { usedIssues: [], creatableIssues: [] }
  );
  const usedIssueCount = usedIssues.length;
  const noneAlreadyUsed = forCategory.some(
    option => option.issue === null && option.isUsed
  );

  const items: DropdownOption[] = [];

  // Once any specific issue has a routing, "All Issue Types" would no
  // longer mean all of them - so it's offered only while none are used yet.
  if (usedIssueCount === 0 && creatableIssues.length > 1) {
    items.push({
      label: t(
        'web.admin.serviceRequest.settings.coverageHours.create.allIssueTypes'
      ),
      value: ALL_ISSUE_TYPES_VALUE,
    });
  }

  if (!noneAlreadyUsed) {
    items.push({
      label: t(
        'web.admin.serviceRequest.settings.coverageHours.create.none'
      ),
      value: NONE_ISSUE_TYPE_VALUE,
    });
  }

  items.push(
    ...creatableIssues.map(option => ({
      label: option.issue,
      value: option.issue,
    }))
  );

  return items;
}
